/**
 * Sponsor Financial Tracking
 * Monitor budget, spending, and cost metrics
 */

import React, { useState, useEffect } from 'react';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { LoadingSpinner } from '../ui/LoadingSpinner';
import { apiClient } from '@/services/apiClient';
import { DollarSign, TrendingUp, AlertCircle, Building2 } from 'lucide-react';
import ProgramSelector from '../shared/ProgramSelector';

import { useAppTranslation } from '@/contexts/TranslationContext';
interface SponsorFinancialTrackingProps {
  sponsorId: number;
  initialProgramId?: number;
}

interface FinancialSummary {
  fiscal_year: number;
  cbos_count: number;
  enrollments_count: number;
  total_committed: number;
  total_paid: number;
  total_pending: number;
  avg_cost_per_enrollment: number;
}

export const SponsorFinancialTracking: React.FC<SponsorFinancialTrackingProps> = ({
  sponsorId,
  initialProgramId
}) => {
  const { t } = useAppTranslation();
  const [summary, setSummary] = useState<FinancialSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [fiscalYear, setFiscalYear] = useState(new Date().getFullYear());
  const [selectedProgramId, setSelectedProgramId] = useState<number | null>(initialProgramId || null);

  useEffect(() => {
    fetchFinancialSummary();
  }, [sponsorId, fiscalYear, selectedProgramId]);

  const fetchFinancialSummary = async () => {
    try {
      setLoading(true);
      let url = `/api/sponsors/${sponsorId}/financial-summary?fiscal_year=${fiscalYear}`;
      if (selectedProgramId) {
        url += `&program_id=${selectedProgramId}`;
      }
      const response = await apiClient.get(url);
      setSummary(response.data);
    } catch (err) {
      console.error('Error fetching financial summary:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <LoadingSpinner />;
  if (!summary) return null;

  const utilizationRate = summary.total_committed > 0
    ? (summary.total_paid / summary.total_committed) * 100
    : 0;

  return (
    <div className="space-y-6">
      {/* Header with Program Selector */}
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 shadow-sm">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <h2 className="text-2xl font-bold">{t('sponsors.financial_tracking', 'Financial Tracking')}</h2>
          <div className="flex items-center gap-4">
            <ProgramSelector
              selectedProgramId={selectedProgramId}
              onProgramChange={setSelectedProgramId}
              sponsorId={sponsorId}
              label={t('sponsors.program', 'Program')}
            />
            <select
              value={fiscalYear}
              onChange={(e) => setFiscalYear(Number(e.target.value))}
              className="px-4 py-2 border rounded-lg"
            >
              <option value={2024}>FY 2024</option>
              <option value={2025}>FY 2025</option>
              <option value={2026}>FY 2026</option>
            </select>
          </div>
        </div>
      </div>

      {/* Financial Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-300">{t('sponsors.total_committed', 'Total Committed')}</p>
              <p className="text-2xl font-bold mt-2">
                ${summary.total_committed.toLocaleString()}
              </p>
            </div>
            <DollarSign className="w-10 h-10 text-blue-500" />
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-300">{t('sponsors.total_paid', 'Total Paid')}</p>
              <p className="text-2xl font-bold mt-2 text-green-600">
                ${summary.total_paid.toLocaleString()}
              </p>
            </div>
            <DollarSign className="w-10 h-10 text-green-500" />
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-300">{t('sponsors.pending', 'Pending')}</p>
              <p className="text-2xl font-bold mt-2 text-orange-600">
                ${summary.total_pending.toLocaleString()}
              </p>
            </div>
            <AlertCircle className="w-10 h-10 text-orange-500" />
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-300">{t('sponsors.avg_cost', 'Avg Cost')}</p>
              <p className="text-2xl font-bold mt-2">
                ${summary.avg_cost_per_enrollment.toFixed(2)}
              </p>
            </div>
            <TrendingUp className="w-10 h-10 text-purple-500" />
          </div>
        </Card>
      </div>

      {/* Budget Utilization */}
      <Card className="p-6">
        <h3 className="text-lg font-semibold mb-4">{t('sponsors.budget_utilization', 'Budget Utilization')}</h3>
        <div className="space-y-4">
          <div>
            <div className="flex justify-between mb-2">
              <span className="text-sm text-gray-600 dark:text-gray-300">{t('sponsors.utilization_rate', 'Utilization Rate')}</span>
              <span className="text-sm font-medium">{utilizationRate.toFixed(1)}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2.5">
              <div
                className="bg-blue-600 h-2.5 rounded-full"
                style={{ width: `${Math.min(utilizationRate, 100)}%` }}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4 pt-4">
            <div className="text-center p-4 bg-blue-50 rounded-lg">
              <p className="text-sm text-gray-600 dark:text-gray-300">{t('sponsors.partner_cbos', 'Partner CBOs')}</p>
              <p className="text-2xl font-bold">{summary.cbos_count}</p>
            </div>
            <div className="text-center p-4 bg-green-50 rounded-lg">
              <p className="text-sm text-gray-600 dark:text-gray-300">{t('sponsors.total_enrollments', 'Total Enrollments')}</p>
              <p className="text-2xl font-bold">{summary.enrollments_count}</p>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
};
